import { useState, useEffect, useMemo, useCallback } from 'react';
import { getAllPartnerChargerTransactionDetails } from '../services/partner-service';
import { ChargerAttributes } from '../models/charger-model';
import { SparklineDataPoint } from '../components/graphs/SparklineChart';
import { isSuccessful } from '../services/api-requests';

interface UseChargerSparklineDataProps {
  chargers: ChargerAttributes[];
  enabled?: boolean;
}

interface ChargerSparklineData {
  [chargerId: string]: SparklineDataPoint[];
}

export const useChargerSparklineData = ({ 
  chargers, 
  enabled = true 
}: UseChargerSparklineDataProps) => {
  // Initialize with 7 days of zero data for all chargers
  const initializeSparklineData = (chargerList: ChargerAttributes[]): ChargerSparklineData => {
    const initialData: ChargerSparklineData = {};
    const endDate = new Date();
    
    chargerList.forEach((charger) => {
      if (charger.uuid) {
        initialData[charger.uuid] = Array.from({ length: 7 }, (_, index) => {
          const date = new Date(endDate);
          date.setDate(date.getDate() - (6 - index)); // Start from 6 days ago to today
          return {
            day: date.toISOString().split('T')[0],
            totalKwh: 0,
          };
        });
      }
    });
    
    return initialData;
  };

  const [sparklineData, setSparklineData] = useState<ChargerSparklineData>(() => 
    initializeSparklineData(chargers)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate date range (exactly 7 days: 6 days ago to today)
  const dateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 6); // 6 days ago + today = 7 days total
    
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  }, []);

  // Process charger history data into daily aggregates (last 7 days only)
  const processChargerHistory = (chargerHistory: any[], chargerId: string): SparklineDataPoint[] => {
    // console.log('processChargerHistory called for', chargerId, 'with history length:', chargerHistory?.length || 0);

    // Create a map to aggregate kWh by date
    const dailyData = new Map<string, number>();

    // Initialize exactly 7 days with 0 - from 6 days ago to today (UTC day string)
    const today = new Date();
    const dayKeys: string[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dayKey = date.toISOString().split('T')[0];
      dailyData.set(dayKey, 0);
      dayKeys.push(dayKey);
    }
    const validDayKeySet = new Set(dayKeys);

    // Aggregate actual data if it exists
    if (Array.isArray(chargerHistory) && chargerHistory.length > 0) {
      chargerHistory.forEach((history, idx) => {
        const rawDate: string | null = history?.service_date || history?.charging_end_time || history?.charging_start_time || null;
        const totalKwhRaw = history?.total_kwh;
        const totalKwh = typeof totalKwhRaw === 'number' && isFinite(totalKwhRaw) ? totalKwhRaw : Number(totalKwhRaw) || 0;

        if (!rawDate) {
        //   console.log('history item missing date fields, skipping', { chargerId, idx, history });
          return;
        }

        const parsed = new Date(rawDate);
        if (isNaN(parsed.getTime())) {
        //   console.log('invalid history date, skipping', { chargerId, idx, rawDate });
          return;
        }

        const historyDayKey = parsed.toISOString().split('T')[0];
        const inRange = validDayKeySet.has(historyDayKey);

        if (!inRange) {
        //   console.log('history outside range, skipping', { chargerId, idx, historyDayKey });
          return;
        }

        const currentTotal = dailyData.get(historyDayKey) || 0;
        dailyData.set(historyDayKey, currentTotal + (totalKwh));
        // console.log('aggregated history', { chargerId, idx, historyDayKey, added: totalKwh, newTotal: currentTotal + totalKwh });
      });
    }

    // Convert to array format sorted by date - ALWAYS return 7 days
    const result = Array.from(dailyData.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, totalKwh]) => ({
        day,
        totalKwh,
      }));

    // console.log('processChargerHistory result for', chargerId, ':', result);
    return result;
  };

  const fetchSparklineData = useCallback(async () => {
    // console.log('fetchSparklineData called', { enabled, chargersLength: chargers.length });
    
    if (!enabled || chargers.length === 0) {
    //   console.log('fetchSparklineData: skipping due to enabled/chargers', { enabled, chargersLength: chargers.length });
      return;
    }

    setLoading(true);
    setError(null);

    try {
    //   console.log('fetchSparklineData: calling API with dateRange', dateRange);
      const response = await getAllPartnerChargerTransactionDetails(dateRange);
      
    //   console.log('fetchSparklineData: API response', { status: response.status, hasPayload: !!response.payload });
      
      if (isSuccessful(response.status) && response.payload?.chargers) {
        const newSparklineData: ChargerSparklineData = {};
        
        // console.log('response.payload.chargers', response.payload.chargers);

        // Process each charger's data
        response.payload.chargers.forEach((chargerData) => {
          const chargerId = chargerData.charger?.uuid || chargerData.chargerId;
        //   console.log('Processing charger data for:', chargerId, {
        //     hasChargerHistory: !!chargerData.chargerHistory,
        //     historyLength: chargerData.chargerHistory?.length || 0
        //   });
          
          if (chargerId) {
            // Always process, even if chargerHistory is empty
            newSparklineData[chargerId] = processChargerHistory(
              chargerData.chargerHistory || [],
              chargerId
            );
          }
        });

        // Ensure all displayed chargers have data (7 days with zeros if no actual data)
        chargers.forEach((charger) => {
          if (charger.uuid && !newSparklineData[charger.uuid]) {
            // Create 7 days of zero data
            const endDate = new Date();
            newSparklineData[charger.uuid] = Array.from({ length: 7 }, (_, index) => {
              const date = new Date(endDate);
              date.setDate(date.getDate() - (6 - index)); // Start from 6 days ago to today
              return {
                day: date.toISOString().split('T')[0],
                totalKwh: 0,
              };
            });
          }
        });

        // console.log('fetchSparklineData: final sparkline data structure:', Object.keys(newSparklineData).map(key => ({
        //   chargerId: key,
        //   dataLength: newSparklineData[key].length,
        //   firstDay: newSparklineData[key][0]?.day,
        //   lastDay: newSparklineData[key][newSparklineData[key].length - 1]?.day
        // })));
        
        setSparklineData(newSparklineData);
      } else {
        // console.log('fetchSparklineData: API call unsuccessful or no data');
        throw new Error('Failed to fetch charger transaction data');
      }
    } catch (err) {
    //   console.error('Error fetching sparkline data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [enabled, dateRange]);

  useEffect(() => {
    // console.log('useChargerSparklineData: chargers changed', chargers.length);
    // Re-initialize sparkline data when chargers change
    setSparklineData(initializeSparklineData(chargers));
    if (enabled && chargers.length > 0) {
      fetchSparklineData();
    }
  }, [chargers.length, enabled]);

  return {
    sparklineData,
    loading,
    error,
    refetch: fetchSparklineData,
  };
};
